import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { 
  Coupon, 
  CouponTemplate, 
  CreateCouponTemplate,
  CouponType,
} from '../../types/coupon';
import { EligibleRule } from '../../types/discounts';
import { getAllSchools } from '../../config/schools';
import { formatDateDisplay, formatDateInBusinessTZ } from '../../utils/date';
import { Edit, Trash2, Gift, PowerOff, Power, Printer, Plus } from 'lucide-react';
import { CouponPreviewModal } from '../components/CouponPreviewModal';
import { EligibleRuleModal } from '../components/EligibleRuleModal';
import { PrintableCouponData } from '../components/PrintableCoupon';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { analytics } from '../../utils/analytics';
import { AdminBrandTag } from '../components/AdminBrandTag';
import { brandNameSuffix } from '../utils/brandFilter';

type CouponsTab = 'templates' | 'printable' | 'coupons' | 'discounts';
const COUPONS_TABS: readonly CouponsTab[] = ['templates', 'printable', 'coupons', 'discounts'];

interface CouponWithTemplate extends Coupon {
  template?: CouponTemplate;
  student_name?: string;
  program_name?: string;
  /** Brand of the invoice the coupon is applied to; undefined for a coupon with no invoice. */
  brand_key?: string;
  parent_name?: string;
}

interface GrantParent { id: string; first_name?: string; last_name?: string; email: string; }
interface GrantInvoice {
  id: string;
  due_date: string;
  status: string;
  total_amount: number;
  student_name: string;
  program_name: string;
  brand_key?: string;
  program_start_date: string;
}

interface GrantForm {
  user_id: string;
  invoice_id: string;
  code: string;
  type: string;
  value: number;
  usage_limit: number | null;
  expires_at: string;
}

// EligibleRule now imported from shared types

interface ParentUser {
  id: string;
  first_name?: string;
  last_name?: string;
  email: string;
}

// Printable coupon form interface
interface PrintableCouponForm {
  themeName: string;
  title: string;
  amount: number;
  numberOfCoupons: number;
  expiresAt: string;
  noExpiration: boolean;
}

export function AdminCoupons() {
  const [activeTab, setActiveTab] = useUrlTabState<CouponsTab>('tab', COUPONS_TABS, 'templates');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Templates state
  const [templates, setTemplates] = useState<CouponTemplate[]>([]);
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CouponTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState<CreateCouponTemplate>({
    name: '',
    description: '',
    type: 'percentage',
    value: 0,
    active: true,
    usage_limit: null,
    default_expiry_days: null,
  });

  // Coupons state
  const [coupons, setCoupons] = useState<CouponWithTemplate[]>([]);
  const [selectedTemplateForGrant, setSelectedTemplateForGrant] = useState<CouponTemplate | null>(null);

  // Add new state for grant modal
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantForm, setGrantForm] = useState<GrantForm>({
    user_id: '',
    invoice_id: '',
    code: '',
    type: 'percentage',
    value: 0,
    usage_limit: null,
    expires_at: '',
  });
  const [grantUsers, setGrantUsers] = useState<GrantParent[]>([]);
  const [grantInvoices, setGrantInvoices] = useState<GrantInvoice[]>([]);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantError, setGrantError] = useState('');
  const [grantTemplateName, setGrantTemplateName] = useState<string>('');

  // Grant Coupon modal search state
  const [grantUserSearch, setGrantUserSearch] = useState('');
  const [grantUserSearchResults, setGrantUserSearchResults] = useState<GrantParent[]>([]);
  const [searchingGrantUsers, setSearchingGrantUsers] = useState(false);

  // Add state for loading coupon deactivation
  const [deactivatingCouponId, setDeactivatingCouponId] = useState<string | null>(null);

  // Row selection state
  const [selectedTemplateRowId, setSelectedTemplateRowId] = useState<string | null>(null);
  const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);

  // Discount Config state — a single list of eligible rules (school/parent/student)
  const [eligibleRules, setEligibleRules] = useState<EligibleRule[]>([]);
  const [allParents, setAllParents] = useState<ParentUser[]>([]);
  const [discountConfigLoading, setDiscountConfigLoading] = useState(false);
  const [discountConfigError, setDiscountConfigError] = useState<string | null>(null);
  const allSchools = getAllSchools();
  const [allStudents, setAllStudents] = useState<{ id: string; first_name: string; last_name: string }[]>([]);

  // Shared add/edit modal for eligible discount rules
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<EligibleRule | null>(null);
  // Selected rule row — actions are only revealed for the selected row.
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);

  // Discount line item configs for eligible rules
  interface DiscountLineItemConfigOption {
    id: string;
    name: string;
    subtype: string | null;
  }
  const [discountLineItemConfigs, setDiscountLineItemConfigs] = useState<DiscountLineItemConfigOption[]>([]);
  const getDiscountConfigName = (id?: string | null): string => {
    if (!id) return '';
    const cfg = discountLineItemConfigs.find(c => c.id === id);
    return cfg ? cfg.name : '';
  };

  // Printable coupons state
  const [printableForm, setPrintableForm] = useState<PrintableCouponForm>({
    themeName: '',
    title: '课程抵用券',
    amount: 20,
    numberOfCoupons: 8,
    expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days default
    noExpiration: false,
  });
  const [printablePreviewOpen, setPrintablePreviewOpen] = useState(false);
  const [printablePreviewCoupons, setPrintablePreviewCoupons] = useState<PrintableCouponData[]>([]);
  const [printableError, setPrintableError] = useState<string | null>(null);

  // Fetch data
  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplates();
    } else if (activeTab === 'coupons') {
      fetchCoupons();
    }
    // Always fetch discount config on mount
    fetchDiscountConfig();
  }, [activeTab]);

  // Fetch users/parents when modal opens
  useEffect(() => {
    if (grantModalOpen) {
      (async () => {
        setGrantLoading(true);
        setGrantError('');
        const { data, error } = await supabase.from('parents').select('id, first_name, last_name, email');
        if (error) setGrantError('Failed to load users');
        setGrantUsers(data || []);
        setGrantLoading(false);
      })();
    }
  }, [grantModalOpen]);

  // Fetch invoices for selected user if invoice-specific
  useEffect(() => {
    if (grantModalOpen && grantForm.user_id) {
      (async () => {
        setGrantLoading(true);
        setGrantError('');
        // 1. Get all students for the parent
        const { data: students, error: studentsError } = await supabase
          .from('student_parents')
          .select('student_id')
          .eq('parent_id', grantForm.user_id);
        if (studentsError) {
          setGrantError('Failed to load students');
          setGrantInvoices([]);
          setGrantLoading(false);
          return;
        }
        const studentIds = (students || []).map((s: { student_id: string }) => s.student_id);
        if (studentIds.length === 0) {
          setGrantInvoices([]);
          setGrantLoading(false);
          return;
        }
        // 2. Get all active enrollments for those students
        const { data: enrollments, error: enrollmentsError } = await supabase
          .from('enrollments')
          .select('id, student_id, program_id')
          .in('student_id', studentIds)
          .eq('is_active', true);
        if (enrollmentsError) {
          setGrantError('Failed to load enrollments');
          setGrantInvoices([]);
          setGrantLoading(false);
          return;
        }
        const enrollmentIds = (enrollments || []).map((e: { id: string }) => e.id);
        const enrollmentMap = new Map((enrollments || []).map((e: { id: string, student_id: string, program_id: string }) => [e.id, e]));
        if (enrollmentIds.length === 0) {
          setGrantInvoices([]);
          setGrantLoading(false);
          return;
        }
        // 3. Get all invoices for those enrollments, only pending
        const { data: invoices, error: invoicesError } = await supabase
          .from('invoices')
          .select('id, due_date, status, total_amount, enrollment_id')
          .in('enrollment_id', enrollmentIds)
          .eq('status', 'pending');
        if (invoicesError) {
          setGrantError('Failed to load invoices');
          setGrantInvoices([]);
          setGrantLoading(false);
          return;
        }
        // 4. Get all students and programs for those enrollments
        const programIds = Array.from(new Set((enrollments || []).map((e: { program_id: string }) => e.program_id)));
        const { data: studentsData } = await supabase
          .from('students')
          .select('id, first_name, last_name')
          .in('id', studentIds);
        const { data: programsData } = await supabase
          .from('programs')
          .select('id, name, start_date, brand_key')
          .in('id', programIds);
        const studentMap = new Map((studentsData || []).map((s: { id: string, first_name: string, last_name: string }) => [s.id, s]));
        const programMap = new Map((programsData || []).map((p: { id: string, name: string, start_date: string, brand_key: string | null }) => [p.id, p]));
        // 5. Compose GrantInvoice objects
        const grantInvoices: GrantInvoice[] = (invoices || []).map((inv) => {
          const enrollment = enrollmentMap.get(inv.enrollment_id);
          const student = enrollment ? studentMap.get(enrollment.student_id) : undefined;
          const program = enrollment ? programMap.get(enrollment.program_id) : undefined;
          return {
            id: inv.id,
            due_date: inv.due_date,
            status: inv.status,
            total_amount: inv.total_amount,
            student_name: student ? `${student.first_name} ${student.last_name}` : '',
            program_name: program ? program.name : '',
            brand_key: program?.brand_key ?? undefined,
            program_start_date: program ? program.start_date : '',
          };
        });
        setGrantInvoices(grantInvoices);
        setGrantLoading(false);
      })();
    } else {
      setGrantInvoices([]);
    }
  }, [grantModalOpen, grantForm.user_id]);

  async function fetchTemplates() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('coupon_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  }

  async function fetchCoupons() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('coupons')
        .select(`
          *,
          template:coupon_templates(*),
          invoice:invoices!coupons_applicable_invoice_id_fkey(
            id,
            enrollment:enrollments(
              student:students(first_name, last_name),
              program:programs(name, brand_key)
            )
          ),
          parent:parents!coupons_user_id_fkey(id, first_name, last_name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include student and program names
      const transformedData = (data || []).map(coupon => ({
        ...coupon,
        parent_name: coupon.parent
          ? `${coupon.parent.first_name || ''} ${coupon.parent.last_name || ''}`.trim() || coupon.parent.email
          : undefined,
        student_name: coupon.invoice?.enrollment?.student
          ? `${coupon.invoice.enrollment.student.first_name} ${coupon.invoice.enrollment.student.last_name}`
          : undefined,
        program_name: coupon.invoice?.enrollment?.program?.name,
        brand_key: coupon.invoice?.enrollment?.program?.brand_key ?? undefined,
      }));

      setCoupons(transformedData);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  }

  // Template handlers
  async function handleAddTemplate(e: React.FormEvent) {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('coupon_templates')
        .insert([newTemplate]);

      if (error) throw error;

      setIsAddingTemplate(false);
      setNewTemplate({
        name: '',
        description: '',
        type: 'percentage',
        value: 0,
        active: true,
        usage_limit: null,
        default_expiry_days: null,
      });
      setSuccessMessage('Template created successfully!');
      fetchTemplates();
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unknown error occurred');
      }
    }
  }

  async function handleEditTemplate(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedTemplate) return;

    try {
      const { error } = await supabase
        .from('coupon_templates')
        .update(newTemplate)
        .eq('id', selectedTemplate.id);

      if (error) throw error;

      setIsEditingTemplate(false);
      setSelectedTemplate(null);
      setSuccessMessage('Template updated successfully!');
      fetchTemplates();
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unknown error occurred');
      }
    }
  }

  async function handleToggleTemplateStatus(template: CouponTemplate) {
    try {
      const { error } = await supabase
        .from('coupon_templates')
        .update({ active: !template.active })
        .eq('id', template.id);

      if (error) throw error;
      setSuccessMessage(`Template ${template.active ? 'deactivated' : 'activated'} successfully!`);
      fetchTemplates();
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unknown error occurred');
      }
    }
  }

  // Open modal from Grant button, prefill from template
  function openGrantModalFromTemplate(template: CouponTemplate) {
    setGrantTemplateName(template.name || '');
    setSelectedTemplateForGrant(template);
    setGrantForm({
      user_id: '',
      invoice_id: '',
      code: '',
      type: template.type,
      value: template.value,
      usage_limit: template.usage_limit,
      expires_at: '',
    });
    setGrantUserSearch('');
    setGrantUserSearchResults([]);
    setGrantModalOpen(true);
  }

  // Grant coupon handler
  async function handleGrantCoupon(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setGrantLoading(true);
    setGrantError('');
    try {
      const couponData = {
        code: grantForm.code || generateUniqueCode(selectedTemplateForGrant?.name || 'COUPON'),
        type: grantForm.type,
        value: grantForm.value,
        usage_limit: grantForm.usage_limit,
        expires_at: grantForm.expires_at || null,
        user_id: grantForm.user_id || null,
        applicable_invoice_id: grantForm.invoice_id || null,
        template_id: selectedTemplateForGrant?.id || null,
      };
      const { error } = await supabase.from('coupons').insert([couponData]);
      if (error) throw error;

      analytics.trackAdminAction('admin_coupon_grant', {
        entityType: 'coupon',
        metadata: {
          code: couponData.code,
          coupon_type: couponData.type,
          value: couponData.value,
          granted_to_user_id: couponData.user_id,
          invoice_id: couponData.applicable_invoice_id,
          template_id: couponData.template_id,
        },
      });
      setGrantModalOpen(false);
      setGrantUserSearch('');
      setGrantUserSearchResults([]);
      setSuccessMessage('Coupon granted successfully!');
      fetchCoupons();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to grant coupon';
      setGrantError(errorMsg);
    } finally {
      setGrantLoading(false);
    }
  }

  // Helper function to generate unique coupon codes
  function generateUniqueCode(templateName: string): string {
    const prefix = templateName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${prefix}-${random}`;
  }

  // Add handler to deactivate a coupon
  async function handleDeactivateCoupon(couponId: string) {
    setDeactivatingCouponId(couponId);
    setError(null);
    try {
      const today = new Date().toISOString();
      const { error } = await supabase
        .from('coupons')
        .update({ expires_at: today })
        .eq('id', couponId);
      if (error) throw error;
      setSuccessMessage('Coupon deactivated successfully!');
      fetchCoupons();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate coupon');
    } finally {
      setDeactivatingCouponId(null);
    }
  }

  // Generate code for printable coupons using theme name
  function generatePrintableCouponCode(themeName: string, index: number): string {
    const prefix = themeName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 4); // Max 4 chars for prefix
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    const seq = String(index + 1).padStart(2, '0');
    return `${prefix}-${random}${seq}`;
  }

  // Handle preview for printable coupons
  function handlePrintablePreview() {
    setPrintableError(null);
    
    // Validation
    if (!printableForm.themeName.trim()) {
      setPrintableError('Theme name is required');
      return;
    }
    if (!printableForm.title.trim()) {
      setPrintableError('Title is required');
      return;
    }
    if (printableForm.amount <= 0) {
      setPrintableError('Amount must be greater than 0');
      return;
    }
    if (printableForm.numberOfCoupons <= 0 || printableForm.numberOfCoupons % 8 !== 0) {
      setPrintableError('Number of coupons must be a positive multiple of 8');
      return;
    }
    if (!printableForm.noExpiration && !printableForm.expiresAt) {
      setPrintableError('Please set an expiration date or check "No Expiration"');
      return;
    }

    // Generate preview coupons with color variations (2 of each color per 8 coupons)
    const coupons: PrintableCouponData[] = [];
    for (let i = 0; i < printableForm.numberOfCoupons; i++) {
      // Color pattern: 0,0,1,1,2,2,3,3 repeating for each page of 8
      const colorIndex = Math.floor((i % 8) / 2);
      coupons.push({
        code: generatePrintableCouponCode(printableForm.themeName, i),
        amount: printableForm.amount,
        title: printableForm.title.toUpperCase(),
        expiresAt: printableForm.noExpiration ? null : printableForm.expiresAt,
        colorIndex,
      });
    }
    
    setPrintablePreviewCoupons(coupons);
    setPrintablePreviewOpen(true);
  }

  // Save printable coupons to database
  async function savePrintableCoupons(): Promise<void> {
    const couponsToInsert = printablePreviewCoupons.map(coupon => ({
      code: coupon.code,
      type: 'fixed_amount' as const,
      value: coupon.amount,
      usage_limit: 1,
      used_count: 0,
      expires_at: coupon.expiresAt,
      user_id: null, // Not tied to specific user
      applicable_invoice_id: null, // Not tied to specific invoice
      template_id: null,
    }));

    const { error } = await supabase.from('coupons').insert(couponsToInsert);
    if (error) throw error;

    analytics.trackAdminAction('admin_printable_coupons_create', {
      entityType: 'coupon',
      metadata: {
        count: couponsToInsert.length,
        theme_name: printableForm.themeName,
        amount: printableForm.amount,
        expires_at: printableForm.expiresAt,
      },
    });
    
    setSuccessMessage(`Successfully created ${couponsToInsert.length} printable coupons!`);
    
    // Reset form
    setPrintableForm({
      themeName: '',
      title: '课程抵用券',
      amount: 20,
      numberOfCoupons: 8,
      expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      noExpiration: false,
    });
  }

  async function fetchDiscountConfig() {
    try {
      setDiscountConfigLoading(true);
      setDiscountConfigError(null);
      Promise.all([
        supabase.from('eligible_rules').select('*'),
        supabase.from('parents').select('id, first_name, last_name, email'),
        supabase.from('students').select('id, first_name, last_name'),
        supabase.from('line_item_configs').select('id, name, subtype').eq('type', 'discount')
      ]).then(([rulesRes, parentsRes, studentsRes, discountConfigsRes]) => {
        if (rulesRes.error) setDiscountConfigError(rulesRes.error.message);
        if (parentsRes.error) setDiscountConfigError(parentsRes.error.message);
        if (studentsRes.error) setDiscountConfigError(studentsRes.error.message);
        if (discountConfigsRes.error) setDiscountConfigError(discountConfigsRes.error.message);
        const rules = (rulesRes.data || []) as EligibleRule[];
        // Order so student rules (most common) come first, then parent, then school.
        const order: Record<EligibleRule['type'], number> = { student: 0, parent: 1, school: 2 };
        setEligibleRules([...rules].sort((a, b) => order[a.type] - order[b.type]));
        setAllParents(parentsRes.data || []);
        setAllStudents(studentsRes.data || []);
        const opts = (discountConfigsRes.data || []).map((row: { id: string; name: string; subtype: string | null }) => ({ id: row.id, name: row.name, subtype: row.subtype }));
        setDiscountLineItemConfigs(opts);
      }).finally(() => setDiscountConfigLoading(false));
    } catch (error: unknown) {
      if (error instanceof Error) {
        setDiscountConfigError(error.message);
      } else {
        setDiscountConfigError('An unknown error occurred');
      }
    }
  }

  // Open the shared modal to add a new rule.
  const handleAddRule = () => {
    setEditingRule(null);
    setRuleModalOpen(true);
  };
  // Open the shared modal to edit an existing rule.
  const handleEditRule = (rule: EligibleRule) => {
    setEditingRule(rule);
    setRuleModalOpen(true);
  };
  const handleDeleteRule = async (id: string) => {
    if (!window.confirm('Delete this rule?')) return;
    setDiscountConfigLoading(true);
    try {
      await supabase.from('eligible_rules').delete().eq('id', id);
      // Automatic discounts apply at invoice generation, so removing a rule silently changes
      // what every future invoice charges.
      analytics.trackAdminAction('admin_eligible_rule_delete', {
        entityType: 'eligible_rule',
        entityId: id,
      });
      fetchDiscountConfig();
    } finally {
      setDiscountConfigLoading(false);
    }
  };

  // Grant Coupon user search function
  const searchGrantUsers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setGrantUserSearchResults([]);
      return;
    }

    try {
      setSearchingGrantUsers(true);
      const { data, error } = await supabase
        .from('parents')
        .select('id, first_name, last_name, email')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        .eq('active', true)
        .limit(10);

      if (error) throw error;

      // Filter out currently selected user
      const filteredResults = (data || []).filter(parent => parent.id !== grantForm.user_id);
      setGrantUserSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching grant users:', error);
    } finally {
      setSearchingGrantUsers(false);
    }
  };

  // Debounced search effect for Grant Coupon users
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (grantUserSearch) {
        searchGrantUsers(grantUserSearch);
      } else {
        setGrantUserSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [grantUserSearch]);

  // Grant Coupon user selection handlers
  const handleSelectGrantUser = (user: GrantParent) => {
    setGrantForm(f => ({ ...f, user_id: user.id, invoice_id: '' }));
    setGrantUserSearch('');
    setGrantUserSearchResults([]);
  };

  const handleClearGrantUser = () => {
    setGrantForm(f => ({ ...f, user_id: '', invoice_id: '' }));
    setGrantUserSearch('');
    setGrantUserSearchResults([]);
  };
  // Scope display metadata for the unified discount rules table.
  const SCOPE_META: Record<EligibleRule['type'], { label: string; emoji: string; badge: string }> = {
    student: { label: 'Student', emoji: '🧒', badge: 'bg-blue-100 text-blue-800' },
    parent: { label: 'Parent', emoji: '👨‍👩‍👧‍👦', badge: 'bg-purple-100 text-purple-800' },
    school: { label: 'School', emoji: '🏫', badge: 'bg-amber-100 text-amber-800' },
  };

  // Human-readable target(s) for a rule (school name, or resolved parent/student names).
  const ruleTargetText = (rule: EligibleRule): string => {
    if (rule.type === 'school') return rule.value;
    let ids: string[] = [];
    try {
      ids = JSON.parse(rule.value);
    } catch {
      ids = [];
    }
    if (rule.type === 'parent') {
      return ids.map(id => {
        const p = allParents.find(ap => ap.id === id);
        return p ? `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() : id;
      }).join(', ');
    }
    return ids.map(id => {
      const s = allStudents.find(st => st.id === id);
      return s ? `${s.first_name} ${s.last_name}` : id;
    }).join(', ');
  };

  const formatRuleValue = (rule: EligibleRule): string =>
    rule.discount_type === 'percentage' ? `${rule.discount_value}%` : `$${rule.discount_value}`;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <p className="text-xl">Loading...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
        {/* Header with tabs */}
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex-shrink-0">
          <div className="flex justify-between items-center mb-4"></div>
          <div className="border-b border-gray-200 -mx-4 sm:mx-0">
            <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto px-4 sm:px-0">
              <button
                onClick={() => setActiveTab('templates')}
                className={`${activeTab === 'templates' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Templates
              </button>
              <button
                onClick={() => setActiveTab('printable')}
                className={`${activeTab === 'printable' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1`}
              >
                <Printer className="w-4 h-4" />
                Printable
              </button>
              <button
                onClick={() => setActiveTab('coupons')}
                className={`${activeTab === 'coupons' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Coupons
              </button>
              <button
                onClick={() => setActiveTab('discounts')}
                className={`${activeTab === 'discounts' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Discounts
              </button>
            </nav>
          </div>
        </div>

        {error && (
          <div className="m-4 p-4 bg-red-50 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="m-4 p-4 bg-green-50 text-green-700 rounded-md">
            {successMessage}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
            {!isAddingTemplate && !isEditingTemplate && (
              <div className="px-4 py-4 flex justify-end">
                <button
                  onClick={() => setIsAddingTemplate(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add Template
                </button>
              </div>
            )}
            {(isAddingTemplate || isEditingTemplate) && (
              <div className="p-4 border-b border-gray-200">
                <h4 className="text-md font-medium text-gray-700 mb-4">
                  {isAddingTemplate ? 'Add New Template' : 'Edit Template'}
                </h4>
                <form onSubmit={isAddingTemplate ? handleAddTemplate : handleEditTemplate} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="template-name" className="block text-sm font-medium text-gray-700">
                        Template Name
                      </label>
                      <input
                        type="text"
                        id="template-name"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="template-description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <input
                        type="text"
                        id="template-description"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        value={newTemplate.description || ''}
                        onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label htmlFor="template-type" className="block text-sm font-medium text-gray-700">
                        Discount Type
                      </label>
                      <select
                        id="template-type"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        value={newTemplate.type}
                        onChange={(e) => setNewTemplate({ ...newTemplate, type: e.target.value as CouponType })}
                        required
                      >
                        <option value="percentage">Percentage</option>
                        <option value="fixed_amount">Fixed Amount</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="template-value" className="block text-sm font-medium text-gray-700">
                        Value
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        {newTemplate.type === 'percentage' ? (
                          <input
                            type="number"
                            id="template-value"
                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md"
                            value={newTemplate.value}
                            onChange={(e) => setNewTemplate({ ...newTemplate, value: parseFloat(e.target.value) })}
                            min="0"
                            max="100"
                            step="0.01"
                            required
                          />
                        ) : (
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              id="template-value"
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                              value={newTemplate.value}
                              onChange={(e) => setNewTemplate({ ...newTemplate, value: parseFloat(e.target.value) })}
                              min="0.01"
                              step="0.01"
                              required
                            />
                          </div>
                        )}
                        {newTemplate.type === 'percentage' && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">%</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="template-usage-limit" className="block text-sm font-medium text-gray-700">
                        Usage Limit
                      </label>
                      <input
                        type="number"
                        id="template-usage-limit"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        value={newTemplate.usage_limit ?? ''}
                        onChange={(e) => setNewTemplate({ 
                          ...newTemplate, 
                          usage_limit: e.target.value ? parseInt(e.target.value) : null 
                        })}
                        min="1"
                        placeholder="Unlimited"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label htmlFor="template-expiry-days" className="block text-sm font-medium text-gray-700">
                        Default Expiry (Days)
                      </label>
                      <input
                        type="number"
                        id="template-expiry-days"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        value={newTemplate.default_expiry_days ?? ''}
                        onChange={(e) => setNewTemplate({ 
                          ...newTemplate, 
                          default_expiry_days: e.target.value ? parseInt(e.target.value) : null 
                        })}
                        min="1"
                        placeholder="No expiry"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingTemplate(false);
                        setIsEditingTemplate(false);
                        setSelectedTemplate(null);
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {isAddingTemplate ? 'Add Template' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Templates - Mobile Cards */}
            <div className="sm:hidden space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`rounded-lg shadow p-4 cursor-pointer ${selectedTemplateRowId === template.id ? 'bg-blue-50' : 'bg-white'}`}
                  onClick={() => setSelectedTemplateRowId(selectedTemplateRowId === template.id ? null : template.id)}
                >
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-gray-900 break-words">{template.name}</div>
                    {template.description && (
                      <div className="mt-1 text-xs text-gray-600 break-words">{template.description}</div>
                    )}
                    <div className="mt-2 text-xs text-gray-600"><span className="font-medium">Type:</span> {template.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}</div>
                    <div className="mt-1 text-xs text-gray-600"><span className="font-medium">Value:</span> {template.type === 'percentage' ? `${template.value}%` : `$${template.value.toFixed(2)}`}</div>
                    <div className="mt-1 text-xs text-gray-600"><span className="font-medium">Status:</span> {template.active ? 'Active' : 'Inactive'}</div>
                  </div>
                  {selectedTemplateRowId === template.id && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); openGrantModalFromTemplate(template); }}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                        aria-label={`Grant coupon from ${template.name}`}
                      >
                        <Gift className="h-3 w-3 mr-1" />
                        Grant
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedTemplate(template); setNewTemplate(template); setIsEditingTemplate(true); }}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                        aria-label={`Edit ${template.name}`}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleToggleTemplateStatus(template); }}
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium transition-colors ${template.active ? 'text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900' : 'text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900'}`}
                        aria-label={`${template.active ? 'Deactivate' : 'Activate'} ${template.name}`}
                      >
                        {template.active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                        {template.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {templates.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6">No templates found</div>
              )}
            </div>

            {/* Templates Table (Desktop) */}
            <div className="hidden sm:block overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {templates.map((template) => (
                    <React.Fragment key={template.id}>
                      <tr
                        className={selectedTemplateRowId === template.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}
                        onClick={() => setSelectedTemplateRowId(selectedTemplateRowId === template.id ? null : template.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{template.name}</div>
                          {template.description && (
                            <div className="text-sm text-gray-500">{template.description}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {template.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {template.type === 'percentage'
                            ? `${template.value}%`
                            : `$${template.value.toFixed(2)}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            template.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {template.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                      {selectedTemplateRowId === template.id && (
                        <tr key={`${template.id}-actions`}>
                          <td colSpan={4} className="px-6 py-3 bg-blue-50 border-b border-blue-100">
                            <div className="flex flex-wrap gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); openGrantModalFromTemplate(template); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                              >
                                <Gift className="h-3 w-3 mr-1" />
                                Grant
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedTemplate(template); setNewTemplate(template); setIsEditingTemplate(true); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Edit
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); void handleToggleTemplateStatus(template); }}
                                className={`inline-flex items-center px-2 py-1 text-xs font-medium transition-colors ${
                                  template.active
                                    ? 'text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900'
                                    : 'text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900'
                                }`}
                              >
                                {template.active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                                {template.active ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <div className="lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
            {/* Coupons - Mobile Cards */}
            <div className="sm:hidden space-y-3">
              {coupons.map((coupon) => (
                <div
                  key={coupon.id}
                  className={`rounded-lg shadow p-4 cursor-pointer ${selectedCouponId === coupon.id ? 'bg-blue-50' : 'bg-white'}`}
                  onClick={() => setSelectedCouponId(selectedCouponId === coupon.id ? null : coupon.id)}
                >
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-gray-900 break-words">{coupon.code}</div>
                    <div className="mt-1 text-xs text-gray-600">
                      <span className="font-medium">Template:</span> {coupon.template?.name || 'Custom'}
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      <span className="font-medium">Value:</span> {coupon.type === 'percentage' ? `${coupon.value}%` : `$${coupon.value.toFixed(2)}`}
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      <span className="font-medium">Usage:</span> {coupon.used_count}{coupon.usage_limit ? ` / ${coupon.usage_limit}` : ''}
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      <span className="font-medium">Status:</span>{' '}
                      {!coupon.expires_at || new Date(coupon.expires_at) > new Date() ? 'Active' : 'Expired'}
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      <span className="font-medium">Expires:</span>{' '}
                      {coupon.expires_at ? formatDateInBusinessTZ(coupon.expires_at) : 'Never'}
                    </div>
                    {coupon.parent_name && (
                      <div className="mt-1 text-xs text-gray-500">Parent: {coupon.parent_name}</div>
                    )}
                    {coupon.student_name && (
                      <div className="mt-1 text-xs text-gray-500">Student: {coupon.student_name}{coupon.program_name && ` (${coupon.program_name})`} <AdminBrandTag brand={coupon.brand_key} /></div>
                    )}
                  </div>
                  {selectedCouponId === coupon.id && (!coupon.expires_at || new Date(coupon.expires_at) > new Date()) && (
                    <div className="mt-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleDeactivateCoupon(coupon.id); }}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors disabled:opacity-50"
                        disabled={deactivatingCouponId === coupon.id}
                        aria-label={`Deactivate ${coupon.code}`}
                      >
                        <PowerOff className="h-3 w-3 mr-1" />
                        {deactivatingCouponId === coupon.id ? 'Deactivating...' : 'Deactivate'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {coupons.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6">No coupons found</div>
              )}
            </div>

            {/* Coupons Table (Desktop) */}
            <div className="hidden sm:block overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Template
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Usage
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Expires
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {coupons.map((coupon) => (
                    <React.Fragment key={coupon.id}>
                      <tr
                        className={selectedCouponId === coupon.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}
                        onClick={() => setSelectedCouponId(selectedCouponId === coupon.id ? null : coupon.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{coupon.code}</div>
                          {coupon.parent_name && (
                            <div className="text-sm text-gray-500">Parent: {coupon.parent_name}</div>
                          )}
                          {coupon.student_name && (
                            <div className="text-sm text-gray-500">
                              Student: {coupon.student_name}
                              {coupon.program_name && ` (${coupon.program_name})`}
                              {' '}<AdminBrandTag brand={coupon.brand_key} />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {coupon.template?.name || 'Custom'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {coupon.type === 'percentage'
                            ? `${coupon.value}%`
                            : `$${coupon.value.toFixed(2)}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {coupon.used_count}
                          {coupon.usage_limit ? ` / ${coupon.usage_limit}` : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            !coupon.expires_at || new Date(coupon.expires_at) > new Date()
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {!coupon.expires_at || new Date(coupon.expires_at) > new Date()
                              ? 'Active'
                              : 'Expired'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {coupon.expires_at
                            ? formatDateInBusinessTZ(coupon.expires_at)
                            : 'Never'}
                        </td>
                      </tr>
                      {selectedCouponId === coupon.id && (
                        <tr key={`${coupon.id}-actions`}>
                          <td colSpan={6} className="px-6 py-3 bg-blue-50 border-b border-blue-100">
                            {(!coupon.expires_at || new Date(coupon.expires_at) > new Date()) && (
                              <button
                                onClick={() => void handleDeactivateCoupon(coupon.id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors disabled:opacity-50"
                                disabled={deactivatingCouponId === coupon.id}
                              >
                                <PowerOff className="h-3 w-3 mr-1" />
                                {deactivatingCouponId === coupon.id ? 'Deactivating...' : 'Deactivate'}
                              </button>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Eligible Discounts Tab */}
        {activeTab === 'discounts' && (
        <div className="overflow-y-auto lg:flex-1 lg:min-h-0">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 bg-blue-50 flex-shrink-0">
            <h3 className="text-lg leading-6 font-semibold text-blue-900">Eligible Discounts</h3>
            <p className="text-sm text-blue-700 mt-1">Manage automatic discounts for eligible students, parents, and schools. These rules are applied automatically during invoice creation.</p>
            {/* Verified against create_invoice_on_enrollment on prod (2026-09-17): the rule loops
                sit inside `IF v_program_billing_type = 'recurring_monthly'`, and no rule carries a
                brand. Kaleido sells nothing monthly, so the "resident discount as a parent rule"
                path lands on the family's BHCS invoices instead. Say so where the rule is made. */}
            <div role="note" className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Rules apply only to monthly-billed programs, which today means BHCS after-school. No Kaleido
              program bills monthly, so a rule never reaches a Kaleido invoice. A parent rule added for a
              Kaleido discount would discount that family&apos;s BHCS invoices instead — use a coupon on the
              Kaleido invoice. For Foster City residents, add the &ldquo;Discount for Foster City Residents&rdquo;
              line item to the Kaleido program&apos;s pricing instead.
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Card header */}
              <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 border-b border-gray-200">
                <div>
                  <h4 className="text-base font-semibold text-gray-900">Discount Rules</h4>
                  <p className="text-sm text-gray-500">{eligibleRules.length} active {eligibleRules.length === 1 ? 'rule' : 'rules'}</p>
                </div>
                <button
                  onClick={handleAddRule}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Plus className="h-4 w-4" /> Add rule
                </button>
              </div>

              {discountConfigError && (
                <div className="m-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{discountConfigError}</div>
              )}

              {/* Empty state */}
              {!discountConfigLoading && eligibleRules.length === 0 && (
                <div className="px-4 py-12 text-center">
                  <p className="text-sm text-gray-500">No discount rules yet.</p>
                  <button onClick={handleAddRule} className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium">
                    <Plus className="h-4 w-4" /> Add your first rule
                  </button>
                </div>
              )}

              {discountConfigLoading && eligibleRules.length === 0 && (
                <div className="px-4 py-12 text-center text-sm text-gray-500">Loading...</div>
              )}

              {/* Mobile card list */}
              {eligibleRules.length > 0 && (
                <div className="sm:hidden divide-y divide-gray-100">
                  {eligibleRules.map(rule => {
                    const meta = SCOPE_META[rule.type];
                    const isSelected = selectedRuleId === rule.id;
                    return (
                      <div
                        key={rule.id}
                        className={`p-4 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                        onClick={() => setSelectedRuleId(isSelected ? null : rule.id)}
                      >
                        <div className="min-w-0">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.badge}`}>
                            <span>{meta.emoji}</span> {meta.label}
                          </span>
                          <div className="mt-1.5 text-sm font-medium text-gray-900 break-words">{ruleTargetText(rule)}</div>
                          <div className="mt-1 text-xs text-gray-600">
                            {rule.discount_type === 'percentage' ? 'Percentage' : 'Fixed Amount'} · <span className="font-medium">{formatRuleValue(rule)}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">{getDiscountConfigName(rule.line_item_config_id) || '—'}</div>
                          {rule.notes && <div className="mt-1 text-xs text-gray-500">{rule.notes}</div>}
                        </div>
                        {isSelected && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors" onClick={(e) => { e.stopPropagation(); handleEditRule(rule); }} aria-label="Edit rule">
                              <Edit className="h-3 w-3 mr-1" /> Edit
                            </button>
                            <button className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors" onClick={(e) => { e.stopPropagation(); handleDeleteRule(rule.id); }} aria-label="Delete rule">
                              <Trash2 className="h-3 w-3 mr-1" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Desktop table */}
              {eligibleRules.length > 0 && (
                <div className="hidden sm:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Scope</th>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Target(s)</th>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Type</th>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Value</th>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Config</th>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {eligibleRules.map(rule => {
                        const meta = SCOPE_META[rule.type];
                        const isSelected = selectedRuleId === rule.id;
                        return (
                          <React.Fragment key={rule.id}>
                            <tr
                              className={isSelected ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}
                              onClick={() => setSelectedRuleId(isSelected ? null : rule.id)}
                            >
                              <td className="px-4 py-2.5">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.badge}`}>
                                  <span>{meta.emoji}</span> {meta.label}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-gray-900 max-w-xs">{ruleTargetText(rule)}</td>
                              <td className="px-4 py-2.5 text-gray-600">{rule.discount_type === 'percentage' ? 'Percentage' : 'Fixed Amount'}</td>
                              <td className="px-4 py-2.5 font-medium text-gray-900">{formatRuleValue(rule)}</td>
                              <td className="px-4 py-2.5 text-gray-600">{getDiscountConfigName(rule.line_item_config_id) || '—'}</td>
                              <td className="px-4 py-2.5 text-gray-500">{rule.notes || ''}</td>
                            </tr>
                            {isSelected && (
                              <tr className="bg-blue-50">
                                <td colSpan={6} className="px-4 py-3 border-b border-blue-100">
                                  <div className="flex flex-wrap gap-2">
                                    <button className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors" onClick={(e) => { e.stopPropagation(); handleEditRule(rule); }} title="Edit">
                                      <Edit className="h-3 w-3 mr-1" /> Edit
                                    </button>
                                    <button className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors" onClick={(e) => { e.stopPropagation(); handleDeleteRule(rule.id); }} title="Delete">
                                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Printable Coupons Tab */}
        {activeTab === 'printable' && (
          <div className="overflow-y-auto lg:flex-1 lg:min-h-0">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6 bg-green-50 flex-shrink-0">
              <h3 className="text-lg leading-6 font-semibold text-green-900">Generate Printable Coupons</h3>
              <p className="mt-1 text-sm text-green-700">
                Create physical coupons for distribution. These coupons are not tied to any specific user or invoice.
              </p>
            </div>

            <div className="p-6">
              <div className="max-w-2xl mx-auto">
              <form onSubmit={(e) => { e.preventDefault(); handlePrintablePreview(); }} className="space-y-6">
                {printableError && (
                  <div className="p-4 bg-red-50 text-red-700 rounded-md">
                    {printableError}
                  </div>
                )}
                
                {/* Theme Name */}
                <div>
                  <label htmlFor="theme-name" className="block text-sm font-medium text-gray-700">
                    Theme Name <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Used to generate coupon code prefix (e.g., "Chinese New Year" → CNY-XXXX)
                  </p>
                  <input
                    type="text"
                    id="theme-name"
                    className="mt-2 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={printableForm.themeName}
                    onChange={(e) => setPrintableForm({ ...printableForm, themeName: e.target.value })}
                    placeholder="e.g., Chinese New Year, Summer Camp 2026"
                    required
                  />
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="coupon-title" className="block text-sm font-medium text-gray-700">
                    Coupon Title <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    The main text displayed on the coupon (e.g., "课程抵用券", "GIFT CARD")
                  </p>
                  <input
                    type="text"
                    id="coupon-title"
                    className="mt-2 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={printableForm.title}
                    onChange={(e) => setPrintableForm({ ...printableForm, title: e.target.value })}
                    placeholder="课程抵用券"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Amount */}
                  <div>
                    <label htmlFor="coupon-amount" className="block text-sm font-medium text-gray-700">
                      Amount ($) <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Dollar value of each coupon
                    </p>
                    <div className="mt-2 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        id="coupon-amount"
                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 sm:text-sm border-gray-300 rounded-md"
                        value={printableForm.amount}
                        onChange={(e) => setPrintableForm({ ...printableForm, amount: parseFloat(e.target.value) || 0 })}
                        min="1"
                        step="1"
                        required
                      />
                    </div>
                  </div>

                  {/* Number of Coupons */}
                  <div>
                    <label htmlFor="num-coupons" className="block text-sm font-medium text-gray-700">
                      Number of Coupons <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Must be a multiple of 8 (8 per page)
                    </p>
                    <select
                      id="num-coupons"
                      className="mt-2 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      value={printableForm.numberOfCoupons}
                      onChange={(e) => setPrintableForm({ ...printableForm, numberOfCoupons: parseInt(e.target.value) })}
                      required
                    >
                      <option value={8}>8 (1 page)</option>
                      <option value={16}>16 (2 pages)</option>
                      <option value={24}>24 (3 pages)</option>
                      <option value={32}>32 (4 pages)</option>
                      <option value={40}>40 (5 pages)</option>
                      <option value={48}>48 (6 pages)</option>
                      <option value={56}>56 (7 pages)</option>
                      <option value={64}>64 (8 pages)</option>
                      <option value={80}>80 (10 pages)</option>
                      <option value={100}>100 (~13 pages)</option>
                    </select>
                  </div>
                </div>

                {/* Expiration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Expiration Date
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    When these coupons will expire
                  </p>
                  <div className="mt-2 flex items-center gap-4">
                    <input
                      type="date"
                      className="focus:ring-blue-500 focus:border-blue-500 block shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                      value={printableForm.expiresAt}
                      onChange={(e) => setPrintableForm({ ...printableForm, expiresAt: e.target.value })}
                      disabled={printableForm.noExpiration}
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={printableForm.noExpiration}
                        onChange={(e) => setPrintableForm({ ...printableForm, noExpiration: e.target.checked })}
                      />
                      No Expiration
                    </label>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Summary</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>{printableForm.numberOfCoupons}</strong> coupons will be generated</li>
                    <li>• Each coupon worth <strong>${printableForm.amount}</strong></li>
                    <li>• Total value: <strong>${printableForm.numberOfCoupons * printableForm.amount}</strong></li>
                    <li>• Will print on <strong>{Math.ceil(printableForm.numberOfCoupons / 8)}</strong> letter-size page(s)</li>
                    <li>• Code prefix: <strong>{printableForm.themeName ? printableForm.themeName.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 4) : '...'}</strong></li>
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                    Preview Coupons
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Coupon Preview Modal */}
      <CouponPreviewModal
        isOpen={printablePreviewOpen}
        onClose={() => setPrintablePreviewOpen(false)}
        coupons={printablePreviewCoupons}
        onConfirmGenerate={savePrintableCoupons}
        themeName={printableForm.themeName}
      />

      {/* Eligible Discount Rule Modal (add/edit) */}
      <EligibleRuleModal
        isOpen={ruleModalOpen}
        onClose={() => setRuleModalOpen(false)}
        onSaved={fetchDiscountConfig}
        rule={editingRule}
        schools={allSchools}
        discountConfigs={discountLineItemConfigs}
        parents={allParents}
        students={allStudents}
      />

      {/* Grant Coupon Modal */}
      {grantModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex flex-col justify-end sm:items-center sm:justify-center z-[60]"
             onClick={e => { if (e.target === e.currentTarget) { setGrantModalOpen(false); setGrantUserSearch(''); setGrantUserSearchResults([]); } }}>
          <div className="bg-white p-6 rounded-t-2xl sm:rounded-lg shadow-xl sm:max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="sm:hidden flex justify-center -mt-2 mb-4">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">{grantTemplateName}</h3>
            {selectedTemplateForGrant?.description && (
              <div className="text-sm text-gray-500 mb-4">{selectedTemplateForGrant.description}</div>
            )}
            <form onSubmit={handleGrantCoupon} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
                
                {/* Selected User Display */}
                {grantForm.user_id && (
                  <div className="mb-3">
                    <div className="flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-2 rounded-md text-sm">
                      <span>
                        {(() => {
                          const selectedUser = grantUsers.find(u => u.id === grantForm.user_id);
                          return selectedUser 
                            ? `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`
                            : 'Selected User';
                        })()}
                      </span>
                      <button
                        type="button"
                        onClick={handleClearGrantUser}
                        className="text-blue-600 hover:text-blue-800 ml-2"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                {/* User Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search for users..."
                    value={grantUserSearch}
                    onChange={(e) => setGrantUserSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {/* Search Results Dropdown */}
                  {grantUserSearchResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {grantUserSearchResults.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => handleSelectGrantUser(user)}
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                          <button
                            type="button"
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Select
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No Results Message */}
                  {grantUserSearch.length >= 2 && !searchingGrantUsers && grantUserSearchResults.length === 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
                      No users found.
                    </div>
                  )}

                  {/* Loading State */}
                  {searchingGrantUsers && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
                      Searching...
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Invoice (Optional)</label>
                <select
                  className="mt-1 block w-full border-gray-300 rounded-md"
                  value={grantForm.invoice_id}
                  onChange={e => setGrantForm(f => ({ ...f, invoice_id: e.target.value }))}
                >
                  <option value="">Select invoice</option>
                  {grantInvoices.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.program_name}{brandNameSuffix(inv.brand_key)} - {inv.student_name} - {inv.program_start_date ? formatDateDisplay(inv.program_start_date) : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="block w-full pr-10 border-gray-300 rounded-md bg-gray-100 px-3 py-2 text-base text-gray-900" style={{minHeight: '40px', display: 'flex', alignItems: 'center'}}>
                    {grantForm.value}
                    <span className="ml-1 text-gray-500 sm:text-sm">
                      {grantForm.type === 'percentage' ? '%' : '$'}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Expires At</label>
                <input type="date" className="mt-1 block w-full border-gray-300 rounded-md" value={grantForm.expires_at} onChange={e => setGrantForm(f => ({ ...f, expires_at: e.target.value }))} />
              </div>
              {grantError && <div className="text-red-600 text-sm">{grantError}</div>}
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => {
                  setGrantModalOpen(false);
                  setGrantUserSearch('');
                  setGrantUserSearchResults([]);
                }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" disabled={grantLoading}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" disabled={grantLoading}>{grantLoading ? 'Granting...' : 'Grant'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
} 